import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import type { TestBookingRequest, TestBookingResponse } from '@vagusx/clients';
import { getAppointmentClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';

/**
 * Whether the signed-in person may add a test appointment here (ADR-293,
 * ADR-294): a VagusX platform admin acting inside a demo practice listed in the
 * API's `SECTION_TEST_ORGS`. The API decides; everyone else sees no button, and the
 * API refuses them anyway.
 */
export function useCanAddTestBooking(): boolean {
  const access = useQuery({
    queryKey: queryKeys.testBooking.access(),
    queryFn: ({ signal }) => getAppointmentClient().getTestBookingAccess(signal),
    retry: false,
    throwOnError: false,
    staleTime: 60_000,
  });
  return access.data?.access != null;
}

/** Add a test appointment. Errors reach the user through the global mutation toast. */
export function useCreateTestBooking(): UseMutationResult<
  TestBookingResponse,
  Error,
  TestBookingRequest
> {
  const queryClient = useQueryClient();
  return useMutation<TestBookingResponse, Error, TestBookingRequest>({
    mutationFn: (request) => getAppointmentClient().createTestBooking(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all });
    },
  });
}
